"use client";
import { ExtendedConsentManagement } from "@/components/ExtendedConsentManagement/ExtendedConsentManagement";
import { useRef, useState, useMemo } from "react";
import type { GoogleMapByLatLngType } from "../GoogleMap/GoogleMap";

const GoogleMapByLatLng = dynamic(() => import("../GoogleMap/GoogleMap"), {
  ssr: false,
}) as GoogleMapByLatLngType;
import ModalContent from "@/components/_primitives/Modal/ModalContent/ModalContent";
import ModalRoot from "@/components/_primitives/Modal/ModalRoot/ModalRoot";
import ModalPortal from "@/components/_primitives/Modal/ModalPortal/ModalPortal";
import ModalOverlay from "@/components/_primitives/Modal/ModalOverlay/ModalOverlay";
import { EventCard } from "@/components/EventCard/EventCard";
import { ModalClose } from "@/components/_primitives/Modal/ModalClose/ModalClose";
import styles from "./EventMap.module.scss";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { CustomSVG } from "@/components/_primitives/CustomSVG/CustomSVG.client";
import dynamic from "next/dynamic";
import type { EventMapEvent } from "./getEvents";

const ITEMS_PER_PAGE = 2;

const EventMapModuleClient = ({ events }: { events: EventMapEvent[] }) => {
  const { getIcon, getLabel, locale } = useGlobalContext();

  const [selectedEvents, setSelectedEvents] = useState<number[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalPage, setModalPage] = useState<number>(1);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const modalContentEventsRef = useRef<HTMLDivElement | null>(null);

  const start = (modalPage - 1) * ITEMS_PER_PAGE;
  const end = start + ITEMS_PER_PAGE;
  const pages = Math.ceil(selectedEvents.length / ITEMS_PER_PAGE);

  const canNextPage = modalPage < pages;
  const canPrevPage = modalPage > 1;
  const nextPage = () => {
    if (modalPage < pages) {
      setModalPage(modalPage + 1);
      modalContentEventsRef.current?.scrollTo({
        top: 0,
        left: 0,
        behavior: "instant",
      });
    }
  };
  const prevPage = () => {
    if (canPrevPage) {
      setModalPage(modalPage - 1);
      modalContentEventsRef.current?.scrollTo({
        top: 0,
        left: 0,
        behavior: "instant",
      });
    }
  };

  const Pagination = () => {
    return (
      <div className={styles["modal-content-pagination"]}>
        <button
          disabled={!canPrevPage}
          onClick={prevPage}
          className={`button-reset ${styles["pagination-arrow"]}`}
        >
          <CustomSVG
            width={16}
            height={16}
            src={getIcon("arrowLeft")}
            className={`is-flex`}
            wrapper={"span"}
            aria-hidden
            data-disabled={!canPrevPage}
          />
        </button>

        <button
          disabled={!canNextPage}
          onClick={nextPage}
          className={`button-reset ${styles["pagination-arrow"]}`}
        >
          <CustomSVG
            width={16}
            height={16}
            src={getIcon("arrowRight")}
            wrapper={"span"}
            className={`is-flex`}
            aria-hidden
          />
        </button>
      </div>
    );
  };

  const memoizedMap = useMemo(() => {
    return (
      <GoogleMapByLatLng
        clusterer={{
          clusterLabel: getLabel("aria_label_event_map_cluster"),
          onClusterClick(markers) {
            setSelectedEvents(markers.map((m) => m.eventId));
            setModalPage(1);
            setIsModalOpen(true);
          },
        }}
        onMarkerClick={(marker) => {
          setSelectedEvents([marker.eventId]);
          setModalPage(1);
          setIsModalOpen(true);
        }}
        alignment={{
          autoCenterAndZoom: true,
          fallbackZoom: 5,
          fallbackCenter: { lat: 51.163, lng: 10.447 },
        }}
        markers={events.flatMap((event) =>
          event.location
            ? [
                {
                  lat: event.location.address_googlemaps.lat,
                  lng: event.location.address_googlemaps.lng,
                  title: event.title ?? "",
                  eventId: event.id,
                },
              ]
            : [],
        )}
      />
    );
  }, [events, getLabel]);

  return (
    <ExtendedConsentManagement type_consent_management="Google Maps">
      <div className={styles["event-map-container"]} ref={modalRef}>
        {memoizedMap}
        <ModalRoot
          open={isModalOpen}
          setOpen={setIsModalOpen}
          onMountedChange={(mounted) => {
            // keep the events visible during the exit animation
            if (!mounted) {
              setSelectedEvents([]);
              setModalPage(1);
            }
          }}
          absolutePosition
        >
          <ModalPortal portalRef={modalRef.current}>
            <ModalOverlay />
            <ModalContent
              ariaLabel={getLabel(
                "event_map_modal",
                "Ausgewählte Veranstaltungen",
              )}
            >
              <ModalClose />
              <div className={styles["modal-content"]}>
                <div
                  className={styles["modal-content-events"]}
                  data-lenis-prevent
                  ref={modalContentEventsRef}
                >
                  {events
                    .filter((e) => selectedEvents.includes(e.id))
                    .slice(start, end)
                    .map((event, key) => {
                      return (
                        <EventCard
                          key={key}
                          element={event}
                          headlineTag="h2"
                          moduleType=""
                          showBox={false}
                          show_border_radius={false}
                          show_box_shadow={false}
                          showExcerpt={false}
                          datePosition="left-of-time"
                          getLabel={getLabel}
                          locale={locale}
                        />
                      );
                    })}
                </div>
                {pages > 1 && <Pagination />}
              </div>
            </ModalContent>
          </ModalPortal>
        </ModalRoot>
      </div>
    </ExtendedConsentManagement>
  );
};

export default EventMapModuleClient;
